import React, { useState, useEffect } from 'react'
import { withStyles, createStyles } from '@mui/styles'
import { t } from '../../../infrastructure/i18nextHelper'
import { muiOptions, MuiProps } from '../../../infrastructure/materialUiThemeProvider'
import { hasClaim } from '../../../infrastructure/signIn/userContext'
import { Claims } from '../../../infrastructure/signIn/models'
import { api } from '../../../infrastructure/api'
import { ExcelGeneratorContainer } from '../../../infrastructure/excelExport'
import { ColumnDescriptor, TableItem, TextField, NumberField, Select, MultipleSelect } from '../../common/customComponents'
import { MasterDataItem, MasterDataShell, createExcelLines } from './masterDataShell'
import { Site } from './models'
import { applyFilters } from '../../common/filtersHelper'
import { VesselMeanOfTransportation, vesselMeanOfTransportations } from '../../vessels/vesselModels'
import { dealApi } from '../../deals/dealApis'

function JettyMasterData({ classes }: MuiProps) {
    let excelGenerator = ExcelGeneratorContainer.useContainer()

    let [jettys, setJettys] = useState<TableItem<Jetty>[]>([])
    let [sites, setSites] = useState<Site[]>([])
    let [filters, setFilters] = useState<JettyFilters>(noFilters)
    let [dealTypes, setDealTypes] = useState<string[]>([])

    const tBase = 'admin.masterdata.jetty.'

    let load = async () => {
        let jettys = api.get<TableItem<Jetty>[]>('masterdata/jetty')
        let jettysPromise = await jettys
        setJettys(jettysPromise)
        let sites = api.get<Site[]>('masterdata/jetty/site')
        let countrySites = await sites
        setSites(countrySites)
        let dealTypesPromise = await dealApi.getDealTypes()
        setDealTypes(dealTypesPromise)
    }

    let getItems = () => applyFilters(jettys, filters).map(toTableItem)

    useEffect(() => {
        load()
    }, [])

    let onSave = async (item: Jetty & IsNew) => {
        await api.post('masterdata/jetty', { jetty: item, isNew: item.isNew })
        await load()
        return true
    }

    let onDelete = async (ids: string[]) => {
        let toDelete = jettys.filter(x => ids.find(id => id === generateIdString(x)))
        if (toDelete.length === 0) return false
        await api.del('masterdata/jetty', { toDelete })
        await load()
        return true
    }

    let exportExcel = () => {
        excelGenerator.generate({
            filename: 'Jetty.xlsx',
            sheets: [{ name: 'Jettys', lines: createExcelLines(getItems(), columns) }]
        })
    }

    let getDefaultSiteOnChange = (selectedSites: string[], defaultSite: string) =>
        selectedSites.includes(defaultSite)
            ? defaultSite : selectedSites.first()

    let getSites = (sites: string[]) => sites.join(', ')

    let columns: ColumnDescriptor<TableItem<MasterDataItem<Jetty>>>[] = [
        {
            name: t(tBase + 'jettyCode'),
            value: x => x.code,
            columnFilter: { value: filters.code ?? '', onChange: (code: string) => setFilters({ ...filters, code }) }
        },
        {
            name: t(tBase + 'sites'),
            value: x => getSites(x.sites),
            columnFilter: { value: filters.sites ?? '', onChange: (sites: string) => setFilters({ ...filters, sites }) }
        },
        {
            name: t(tBase + 'defaultSite'),
            value: x => x.defaultSite,
            columnFilter: { value: filters.defaultSite ?? '', onChange: (defaultSite: string) => setFilters({ ...filters, defaultSite }) }
        },
        {
            name: t(tBase + 'purchaseDealType'),
            value: x => x.purchaseDealType,
            columnFilter: { value: filters.purchaseDealType ?? '', onChange: (purchaseDealType: string) => setFilters({ ...filters, purchaseDealType }) }
        },
        {
            name: t(tBase + 'saleDealType'),
            value: x => x.saleDealType,
            columnFilter: { value: filters.saleDealType ?? '', onChange: (saleDealType: string) => setFilters({ ...filters, saleDealType }) }
        },
        {
            name: t(tBase + 'defaultMeanOfTransportation'),
            value: x => x.defaultMeanOfTransportation,
            columnFilter: {
                value: filters.defaultMeanOfTransportation ?? '',
                onChange: (defaultMeanOfTransportation: string) => setFilters({ ...filters, defaultMeanOfTransportation })
            }
        },
        {
            name: t(tBase + 'country'),
            value: x => x.country
        },
        {
            name: t(tBase + 'layTime'),
            value: x => x.laytime.toString()
        }
    ]

    let isManager = hasClaim(Claims.MasterdataJettyManager)
    let isWriter = hasClaim(Claims.MasterdataJettyWriter)

    return (
        <div className={classes.container}>
            <MasterDataShell
                tableId={'Jetty-table'}
                headerLabel={t(tBase + 'jettys')}
                itemLabel={t(tBase + 'jetty')}
                isManager={isManager}
                onExportExcel={exportExcel}
                onNew={emptyJetty}
                onDelete={onDelete}
                onSave={onSave}
                items={getItems()}
                columns={columns}>{
                    (selectedItem, setSelectedItem) => (
                        <>
                            <TextField label={t(tBase + 'jettyCode')}
                                disabled={!selectedItem.isNew}
                                text={selectedItem.code}
                                onChange={event => setSelectedItem({ ...selectedItem, code: event.target.value })} />
                            <MultipleSelect label={t(tBase + 'sites')}
                                chips={true}
                                allWhenEmpty={false}
                                disabled={!isManager && !isWriter}
                                values={selectedItem.sites}
                                choices={sites.map(x => ({ value: x.code, text: x.name }))}
                                onChange={sites => setSelectedItem({
                                    ...selectedItem, sites: sites,
                                    defaultSite: getDefaultSiteOnChange(sites, selectedItem.defaultSite)
                                })} />
                            <Select label={t(tBase + 'defaultSite')}
                                disabled={!isManager && !isWriter}
                                value={selectedItem.defaultSite}
                                choices={selectedItem.sites}
                                onChange={val => { if (val) setSelectedItem({ ...selectedItem, defaultSite: val }) }} />
                            <Select label={t(tBase + 'purchaseDealType')}
                                disabled={!isManager && !isWriter}
                                value={selectedItem.purchaseDealType}
                                choices={dealTypes.sort().map(x => ({ value: x, text: x }))}
                                onChange={val => { if (val) setSelectedItem({ ...selectedItem, purchaseDealType: val }) }} />
                            <Select label={t(tBase + 'saleDealType')}
                                disabled={!isManager && !isWriter}
                                value={selectedItem.saleDealType}
                                choices={dealTypes.sort().map(x => ({ value: x, text: x }))}
                                onChange={val => { if (val) setSelectedItem({ ...selectedItem, saleDealType: val }) }} />
                            <Select label={t(tBase + 'defaultMeanOfTransportation')}
                                disabled={!isManager && !isWriter}
                                value={selectedItem.defaultMeanOfTransportation}
                                choices={vesselMeanOfTransportations}
                                onChange={val => { if (val) setSelectedItem({ ...selectedItem, defaultMeanOfTransportation: val }) }} />
                            <NumberField label={t(tBase + 'layTime')}
                                disabled={!isManager && !isWriter}
                                text={selectedItem.laytime}
                                onChange={event => setSelectedItem({ ...selectedItem, laytime: event ?? 0 })} />
                        </>
                    )}
            </MasterDataShell>
        </div >)
}

type IsNew = { isNew: boolean }

let emptyJetty = (): Jetty & IsNew => ({
    code: '',
    country: '',
    sites: [],
    defaultSite: '',
    defaultMeanOfTransportation: 'Ship',
    purchaseDealType: '',
    saleDealType: '',
    laytime: 0,
    isNew: true
})

let generateIdString = (item: Jetty | TableItem<Jetty>): string => item.code

let toTableItem = (jetty: Jetty): TableItem<MasterDataItem<Jetty>> => {
    return {
        id: generateIdString(jetty),
        code: jetty.code,
        country: jetty.country,
        sites: jetty.sites,
        defaultSite: jetty.defaultSite,
        defaultMeanOfTransportation: jetty.defaultMeanOfTransportation,
        purchaseDealType: jetty.purchaseDealType,
        saleDealType: jetty.saleDealType,
        laytime: jetty.laytime,
        isModified: false
    }
}

type Jetty = {
    code: string,
    country: string,
    sites: string[]
    defaultSite: string
    defaultMeanOfTransportation: VesselMeanOfTransportation,
    purchaseDealType: string,
    saleDealType: string,
    laytime: number
}

type JettyFilters = {
    code: string | null,
    country: string | null,
    sites: string | null,
    defaultSite: string | null,
    defaultMeanOfTransportation: string | null,
    purchaseDealType: string | null,
    saleDealType: string | null
}

let noFilters: JettyFilters = {
    code: null,
    country: null,
    sites: null,
    defaultSite: null,
    defaultMeanOfTransportation: null,
    purchaseDealType: null,
    saleDealType: null
}

let styles = (theme) =>
    createStyles({
        container: { height: '100%' }
    })

export default withStyles(styles, muiOptions)(JettyMasterData)